import React, { useCallback, useState, useMemo } from 'react';
import {
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { BaseAdminScreen, ActionButtons } from '@/components';
import { ADMIN_COLORS } from '@/constants/admin';
import { useCreateUserMutation, useAssignRolesMutation } from '@/store/api/admin/userManagementApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { roleNamesAndDescriptions } from '@/types/admin/userManagement';
import { IOrganization, IProperty } from '@/types/admin/navigation';
import { 
  UserAccountInfoSection,
  UserRoleSelectionSection,
  UserPreviewSection,
  CreateUserSuccessModal,
} from '@/components';
import { useCreateUserForm } from '@/components/admin/userManagement/CreateUser/hooks/useCreateUserForm';

type CreateUserScreenProps = StackScreenProps<AdminStackParamList, 'CreateUser'>;

const FORM_STEPS = [
  'Create Account',
  'Assign User Permissions', 
  'Preview'
];

export const CreateUserScreen: React.FC<CreateUserScreenProps> = ({
  navigation,
}) => {
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // API mutations
  const [createUser, { isLoading: isCreateUserLoading }] = useCreateUserMutation();
  const [assignUserRoles, { isLoading: isAssignUserLoading }] = useAssignRolesMutation();

  const isLoading = isCreateUserLoading || isAssignUserLoading;

  // Custom hook for form logic
  const {
    formData,
    errors,
    activeStep,
    handleFieldChange,
    handleNext,
    handleBack,
    validateForm,
    showDiscardAlert,
  } = useCreateUserForm();

  // Cancel handler
  const handleCancel = useCallback(() => {
    showDiscardAlert(() => navigation.goBack());
  }, [showDiscardAlert, navigation]);

  // Form submission
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    try {
      // Step 1: Create user account
      const createResponse = await createUser({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
      }).unwrap();

      if (createResponse.success) {
        // Step 2: Assign roles (matching admin-web implementation)
        const foundProperties = (adminUser.properties || []).filter((prop: IProperty) =>
          formData.properties?.some((selectedProp: string) => prop.marketingName === selectedProp)
        );
        const foundOrgs = (adminUser.organizations || []).filter((org: IOrganization) =>
          formData.organizations?.some((selectedOrg: string) => org.name === selectedOrg)
        );

        // Single API call with arrays (matching admin-web)
        await assignUserRoles({
          email: formData.email,
          roleName: formData.roleName,
          propertyIds: foundProperties.map((prop: IProperty) => prop.id),
          orgIds: foundOrgs.map((org: IOrganization) => org.id),
        }).unwrap();

        setShowSuccessModal(true);
      } else {
        throw new Error('Failed to create user account');
      }
    } catch (error: any) {
      console.error('Create user error:', error);
      
      if (error?.data?.message?.includes('already exists') || 
          error?.message?.includes('already exists')) {
        showApiError(new Error('Email already exists. Please try again.'), 'Failed to create user');
      } else {
        showApiError(error, 'Failed to create user');
      }
    }
  }, [
    formData,
    adminUser.properties,
    adminUser.organizations,
    createUser,
    assignUserRoles,
    validateForm,
    showApiError,
  ]);

  // Success modal close handler
  const handleSuccessClose = useCallback(() => {
    setShowSuccessModal(false);
    navigation.goBack();
  }, [navigation]);

  // Memoized scroll content to prevent unnecessary re-renders
  const scrollContent = useMemo(() => {
    switch (activeStep) {
      case 0:
        return (
          <UserAccountInfoSection
            formData={formData}
            errors={errors}
            onFieldChange={handleFieldChange}
          />
        );
      case 1:
        return (
          <UserRoleSelectionSection
            formData={formData}
            errors={errors}
            onFieldChange={handleFieldChange}
            adminUser={adminUser}
          />
        );
      case 2:
        return (
          <UserPreviewSection
            formData={formData}
          />
        );
      default:
        return null;
    }
  }, [activeStep, formData, errors, handleFieldChange, adminUser]);

  return (
    <>
      <BaseAdminScreen
        screenName="CreateUser"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <KeyboardAvoidingView
          style={styles.container}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {scrollContent}
          </ScrollView>

          <ActionButtons
            isLoading={isLoading}
            onCancel={activeStep > 0 ? handleBack : handleCancel}
            onSubmit={activeStep === FORM_STEPS.length - 1 ? handleSubmit : handleNext}
            submitText={activeStep === FORM_STEPS.length - 1 ? 'Create User' : 'Next'}
            cancelText={activeStep > 0 ? 'Back' : 'Cancel'}
          />
        </KeyboardAvoidingView>
      </BaseAdminScreen>

      {/* Success Modal */}
      <CreateUserSuccessModal
        visible={showSuccessModal}
        onClose={handleSuccessClose}
        userName={`${formData.firstName} ${formData.lastName}`}
      />
    </>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 20,
    flexGrow: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
  },
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100, // Space for action buttons
  },
});
